<template>
  <AdminLayout page-title="Email Management" page-subtitle="Send emails to users and manage email templates">
    <div class="space-y-8">
      <!-- Email Stats Overview -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div class="stat-card">
          <div class="stat-icon bg-gradient-to-br from-emerald-500/20 to-cyan-500/20">
            <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
          </div>
          <div>
            <p class="stat-label">Emails Sent Today</p>
            <p class="stat-value">{{ emailStats.sentToday }}</p>
            <p class="stat-change text-emerald-400">+{{ emailStats.todayIncrease }}% vs yesterday</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon bg-gradient-to-br from-blue-500/20 to-indigo-500/20">
            <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <p class="stat-label">Delivery Rate</p>
            <p class="stat-value">{{ emailStats.deliveryRate }}%</p>
            <p class="stat-change text-emerald-400">Excellent</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon bg-gradient-to-br from-purple-500/20 to-pink-500/20">
            <svg class="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
            </svg>
          </div>
          <div>
            <p class="stat-label">Open Rate</p>
            <p class="stat-value">{{ emailStats.openRate }}%</p>
            <p class="stat-change text-emerald-400">Above average</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon bg-gradient-to-br from-amber-500/20 to-orange-500/20">
            <svg class="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <p class="stat-label">Templates</p>
            <p class="stat-value">{{ emailTemplates.length }}</p>
            <p class="stat-change text-slate-400">Active templates</p>
          </div>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Compose Email -->
        <div class="card">
          <div class="card-header">
            <h3 class="text-lg font-semibold text-white">Compose Email</h3>
            <div class="flex items-center space-x-2">
              <button @click="loadTemplate" class="btn-outline btn-sm">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                Load Template
              </button>
            </div>
          </div>
          <div class="card-body">
            <form @submit.prevent="sendEmail" class="space-y-4">
              <div class="form-group">
                <label class="form-label">Recipients</label>
                <div class="flex items-center space-x-2">
                  <select v-model="emailForm.recipientType" class="form-select flex-1">
                    <option value="individual">Individual User</option>
                    <option value="all_users">All Users</option>
                    <option value="new_users">New Users (Last 7 days)</option>
                    <option value="active_users">Active Users</option>
                    <option value="inactive_users">Inactive Users</option>
                  </select>
                  <button v-if="emailForm.recipientType === 'individual'" type="button" @click="showUserSelector = true" class="btn-outline btn-sm">
                    Select User
                  </button>
                </div>
                <p v-if="emailForm.selectedUsers.length > 0" class="text-sm text-emerald-400 mt-1">
                  {{ emailForm.selectedUsers.length }} user(s) selected
                </p>
              </div>

              <div class="form-group">
                <label class="form-label">Subject</label>
                <input v-model="emailForm.subject" type="text" class="form-input" placeholder="Enter email subject" required />
              </div>

              <div class="form-group">
                <label class="form-label">Email Content</label>
                <div class="border border-slate-600/50 rounded-xl overflow-hidden">
                  <div class="bg-slate-700/30 px-4 py-2 border-b border-slate-600/50 flex items-center space-x-2">
                    <button type="button" @click="formatText('bold')" class="p-1 hover:bg-slate-600/50 rounded text-slate-300">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h4a4 4 0 110 8h-1v2a1 1 0 11-2 0V4zm4 5a2 2 0 100-4H6v4h1z" clip-rule="evenodd"/>
                      </svg>
                    </button>
                    <button type="button" @click="formatText('italic')" class="p-1 hover:bg-slate-600/50 rounded text-slate-300">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M8 2a1 1 0 000 2h1.5L7.75 12H6a1 1 0 100 2h4a1 1 0 100-2H8.25L10 4h2a1 1 0 100-2H8z" clip-rule="evenodd"/>
                      </svg>
                    </button>
                    <div class="h-4 w-px bg-slate-600/50"></div>
                    <button type="button" @click="insertVariable('{{user.name}}')" class="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded hover:bg-emerald-500/30">
                      {name}
                    </button>
                    <button type="button" @click="insertVariable('{{user.email}}')" class="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded hover:bg-emerald-500/30">
                      {email}
                    </button>
                  </div>
                  <textarea 
                    v-model="emailForm.content" 
                    ref="contentTextarea"
                    rows="8" 
                    class="w-full bg-transparent text-white placeholder-slate-400 p-4 resize-none focus:outline-none" 
                    placeholder="Compose your email content here..."
                    required
                  ></textarea>
                </div>
              </div>

              <div class="flex justify-between items-center">
                <div class="flex items-center space-x-4">
                  <label class="flex items-center space-x-2 text-sm text-slate-300">
                    <input v-model="emailForm.saveAsTemplate" type="checkbox" class="rounded border-slate-600 bg-slate-700 text-emerald-500 focus:ring-emerald-500/50" />
                    <span>Save as template</span>
                  </label>
                  <label class="flex items-center space-x-2 text-sm text-slate-300">
                    <input v-model="emailForm.sendImmediately" type="checkbox" class="rounded border-slate-600 bg-slate-700 text-emerald-500 focus:ring-emerald-500/50" />
                    <span>Send immediately</span>
                  </label>
                </div>
                <div class="flex items-center space-x-3">
                  <button type="button" @click="saveDraft" class="btn-outline">
                    Save Draft
                  </button>
                  <button type="submit" :disabled="sending" class="btn-oilbuddy-primary">
                    <svg v-if="sending" class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ sending ? 'Sending...' : 'Send Email' }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        <!-- Email Templates -->
        <div class="card">
          <div class="card-header">
            <h3 class="text-lg font-semibold text-white">Email Templates</h3>
            <button @click="showTemplateModal = true" class="btn-oilbuddy-primary btn-sm">
              <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
              </svg>
              New Template
            </button>
          </div>
          <div class="card-body">
            <div v-if="emailTemplates.length === 0" class="text-center py-8">
              <svg class="w-16 h-16 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <h3 class="text-lg font-medium text-slate-300 mb-2">No templates yet</h3>
              <p class="text-slate-400">Create your first email template to get started.</p>
            </div>

            <div v-else class="space-y-4">
              <div v-for="template in emailTemplates" :key="template.id" class="border border-slate-600/50 rounded-lg p-4 hover:border-emerald-500/30 transition-colors">
                <div class="flex items-start justify-between mb-3">
                  <div>
                    <h4 class="font-medium text-white">{{ template.name }}</h4>
                    <p class="text-sm text-slate-300">{{ template.subject }}</p>
                    <p class="text-xs text-slate-400 mt-1">{{ template.category }} • Last used {{ formatDate(template.lastUsed) }}</p>
                  </div>
                  <span :class="getTemplateStatusBadge(template.status)" class="badge">
                    {{ template.status }}
                  </span>
                </div>
                <div class="flex items-center space-x-2">
                  <button @click="useTemplate(template)" class="text-emerald-400 hover:text-emerald-300 text-sm font-medium">
                    Use Template
                  </button>
                  <button @click="editTemplate(template)" class="text-slate-300 hover:text-slate-200 text-sm font-medium">
                    Edit
                  </button>
                  <button @click="duplicateTemplate(template)" class="text-blue-400 hover:text-blue-300 text-sm font-medium">
                    Duplicate
                  </button>
                  <button @click="deleteTemplate(template)" class="text-red-400 hover:text-red-300 text-sm font-medium">
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Email History -->
      <div class="card">
        <div class="card-header">
          <h3 class="text-lg font-semibold text-white">Email History</h3>
          <div class="flex items-center space-x-2">
            <select v-model="historyFilter" class="form-select text-sm">
              <option value="all">All Emails</option>
              <option value="sent">Sent</option>
              <option value="draft">Drafts</option>
              <option value="failed">Failed</option>
            </select>
            <button @click="refreshHistory" class="btn-outline btn-sm">
              <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd"/>
              </svg>
              Refresh
            </button>
          </div>
        </div>
        <div class="card-body">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-slate-700/50">
              <thead class="bg-slate-700/30">
                <tr>
                  <th class="table-header">Subject</th>
                  <th class="table-header">Recipients</th>
                  <th class="table-header">Status</th>
                  <th class="table-header">Sent Date</th>
                  <th class="table-header">Actions</th>
                </tr>
              </thead>
              <tbody class="bg-slate-800/30 divide-y divide-slate-700/50">
                <tr v-for="email in filteredEmailHistory" :key="email.id" class="hover:bg-slate-700/20">
                  <td class="table-cell">
                    <div>
                      <div class="text-sm font-medium text-white">{{ email.subject }}</div>
                      <div class="text-xs text-slate-400">{{ email.preview }}</div>
                    </div>
                  </td>
                  <td class="table-cell">
                    <div class="text-sm text-slate-300">{{ email.recipientCount }} recipients</div>
                    <div class="text-xs text-slate-400">{{ email.recipientType }}</div>
                  </td>
                  <td class="table-cell">
                    <span :class="getEmailStatusBadge(email.status)" class="badge">
                      {{ email.status }}
                    </span>
                  </td>
                  <td class="table-cell">
                    <div class="text-sm text-slate-300">{{ formatDate(email.sentAt) }}</div>
                    <div class="text-xs text-slate-400">{{ formatTime(email.sentAt) }}</div>
                  </td>
                  <td class="table-cell">
                    <div class="flex items-center space-x-2">
                      <button @click="viewEmail(email)" class="text-emerald-400 hover:text-emerald-300 text-sm font-medium">
                        View
                      </button>
                      <button v-if="email.status === 'draft'" @click="editDraft(email)" class="text-blue-400 hover:text-blue-300 text-sm font-medium">
                        Edit
                      </button>
                      <button v-if="email.status === 'sent'" @click="viewStats(email)" class="text-purple-400 hover:text-purple-300 text-sm font-medium">
                        Stats
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Template Modal -->
    <div v-if="showTemplateModal" class="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-slate-800/30 backdrop-blur-sm rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-700/50">
        <div class="card-header">
          <h3 class="text-lg font-semibold text-white">{{ editingTemplate ? 'Edit Template' : 'Create Template' }}</h3>
          <button @click="closeTemplateModal" class="text-slate-400 hover:text-slate-300">
            <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>
        <div class="card-body">
          <form @submit.prevent="saveTemplate" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="form-group">
                <label class="form-label">Template Name</label>
                <input v-model="templateForm.name" type="text" class="form-input" required />
              </div>
              <div class="form-group">
                <label class="form-label">Category</label>
                <select v-model="templateForm.category" class="form-select" required>
                  <option value="">Select category</option>
                  <option value="welcome">Welcome</option>
                  <option value="notification">Notification</option>
                  <option value="marketing">Marketing</option>
                  <option value="system">System</option>
                  <option value="support">Support</option>
                </select>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">Subject</label>
              <input v-model="templateForm.subject" type="text" class="form-input" required />
            </div>

            <div class="form-group">
              <label class="form-label">Content</label>
              <textarea v-model="templateForm.content" rows="10" class="form-input" required></textarea>
            </div>

            <div class="flex justify-end space-x-3">
              <button type="button" @click="closeTemplateModal" class="btn-outline">
                Cancel
              </button>
              <button type="submit" class="btn-oilbuddy-primary">
                {{ editingTemplate ? 'Update Template' : 'Create Template' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import AdminLayout from './AdminLayout.vue'
import { emailService, type EmailTemplate } from '../../services/emailService'

// State
const showTemplateModal = ref(false)
const showUserSelector = ref(false)
const editingTemplate = ref<any>(null)
const sending = ref(false)
const historyFilter = ref('all')
const contentTextarea = ref<HTMLTextAreaElement>()

// Email form
const emailForm = reactive({
  recipientType: 'individual',
  selectedUsers: [] as any[],
  subject: '',
  content: '',
  saveAsTemplate: false,
  sendImmediately: true
})

// Template form
const templateForm = reactive({
  name: '',
  category: '',
  subject: '',
  content: ''
})

// Email statistics - starting with zero data
const emailStats = ref({
  sentToday: 0,
  todayIncrease: 0,
  deliveryRate: 0,
  openRate: 0
})

// Email templates - starting empty
const emailTemplates = ref([])

// Email history - starting empty
const emailHistory = ref([])

// Computed
const filteredEmailHistory = computed(() => {
  if (historyFilter.value === 'all') return emailHistory.value
  return emailHistory.value.filter((email: any) => email.status === historyFilter.value)
})

// Methods
const sendEmail = async () => {
  sending.value = true
  try {
    // Get recipients based on type
    const recipients = getEmailRecipients()
    
    if (recipients.length === 0) {
      alert('Please select recipients before sending.')
      return
    }

    // Send email using email service
    const result = await emailService.sendEmail({
      to: recipients,
      subject: emailForm.subject,
      content: emailForm.content,
      variables: {
        'company.name': 'OilBuddy AI',
        'support.email': 'support@oilbuddy.ai'
      },
      priority: 'normal'
    })

    if (result.success) {
      // Add to history
      const newEmail = {
        id: result.messageId || Date.now().toString(),
        subject: emailForm.subject,
        preview: emailForm.content.substring(0, 100) + '...',
        recipientCount: recipients.length,
        recipientType: emailForm.recipientType,
        status: 'sent',
        sentAt: new Date(),
        content: emailForm.content
      }
      
      emailHistory.value.unshift(newEmail)
      emailStats.value.sentToday++
      
      // Save as template if requested
      if (emailForm.saveAsTemplate) {
        const template: EmailTemplate = {
          id: Date.now().toString(),
          name: emailForm.subject || 'Untitled Template',
          subject: emailForm.subject,
          content: emailForm.content,
          category: 'notification',
          variables: ['company.name', 'support.email']
        }
        emailTemplates.value.push(template)
      }
      
      // Reset form
      Object.assign(emailForm, {
        recipientType: 'individual',
        selectedUsers: [],
        subject: '',
        content: '',
        saveAsTemplate: false,
        sendImmediately: true
      })
      
      alert('Email sent successfully!')
    } else {
      alert(result.error || 'Failed to send email. Please try again.')
    }
    
  } catch (error) {
    console.error('Error sending email:', error)
    alert('Failed to send email. Please try again.')
  } finally {
    sending.value = false
  }
}

// Get email recipients based on selected type
const getEmailRecipients = () => {
  switch (emailForm.recipientType) {
    case 'individual':
      return emailForm.selectedUsers.map(user => ({
        email: user.email,
        name: user.name
      }))
    case 'all_users':
      // TODO: Get all users from API
      return [
        { email: 'user1@example.com', name: 'John Doe' },
        { email: 'user2@example.com', name: 'Jane Smith' }
      ]
    case 'new_users':
      // TODO: Get new users from API
      return [
        { email: 'newuser@example.com', name: 'New User' }
      ]
    case 'active_users':
      // TODO: Get active users from API
      return [
        { email: 'active@example.com', name: 'Active User' }
      ]
    case 'inactive_users':
      // TODO: Get inactive users from API
      return [
        { email: 'inactive@example.com', name: 'Inactive User' }
      ]
    default:
      return []
  }
}

const getRecipientCount = () => {
  switch (emailForm.recipientType) {
    case 'individual': return emailForm.selectedUsers.length
    case 'all_users': return 150 // Mock count
    case 'new_users': return 25
    case 'active_users': return 120
    case 'inactive_users': return 30
    default: return 0
  }
}

const saveDraft = () => {
  const draft = {
    id: Date.now().toString(),
    subject: emailForm.subject || 'Untitled Draft',
    preview: emailForm.content.substring(0, 100) + '...',
    recipientCount: getRecipientCount(),
    recipientType: emailForm.recipientType,
    status: 'draft',
    sentAt: new Date(),
    content: emailForm.content
  }
  
  emailHistory.value.unshift(draft)
  alert('Draft saved successfully!')
}

const loadTemplate = () => {
  // TODO: Show template selector
  console.log('Loading template...')
}

const useTemplate = (template: any) => {
  emailForm.subject = template.subject
  emailForm.content = template.content
  template.lastUsed = new Date()
}

const editTemplate = (template: any) => {
  editingTemplate.value = template
  Object.assign(templateForm, template)
  showTemplateModal.value = true
}

const duplicateTemplate = (template: any) => {
  const duplicate = {
    ...template,
    id: Date.now().toString(),
    name: template.name + ' (Copy)',
    lastUsed: new Date()
  }
  emailTemplates.value.push(duplicate)
}

const deleteTemplate = (template: any) => {
  if (confirm('Are you sure you want to delete this template?')) {
    const index = emailTemplates.value.findIndex((t: any) => t.id === template.id)
    if (index > -1) {
      emailTemplates.value.splice(index, 1)
    }
  }
}

const saveTemplate = () => {
  if (editingTemplate.value) {
    // Update existing template
    Object.assign(editingTemplate.value, templateForm)
  } else {
    // Create new template
    const newTemplate = {
      id: Date.now().toString(),
      ...templateForm,
      status: 'active',
      lastUsed: new Date(),
      createdAt: new Date()
    }
    emailTemplates.value.push(newTemplate)
  }
  
  closeTemplateModal()
}

const closeTemplateModal = () => {
  showTemplateModal.value = false
  editingTemplate.value = null
  Object.assign(templateForm, {
    name: '',
    category: '',
    subject: '',
    content: ''
  })
}

const formatText = (format: string) => {
  // TODO: Implement text formatting
  console.log('Formatting text:', format)
}

const insertVariable = (variable: string) => {
  if (contentTextarea.value) {
    const textarea = contentTextarea.value
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const text = textarea.value
    
    emailForm.content = text.substring(0, start) + variable + text.substring(end)
    
    // Set cursor position after the inserted variable
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + variable.length
      textarea.focus()
    }, 0)
  }
}

const refreshHistory = () => {
  // TODO: Implement history refresh
  console.log('Refreshing email history...')
}

const viewEmail = (email: any) => {
  // TODO: Implement email viewing
  console.log('Viewing email:', email.id)
}

const editDraft = (email: any) => {
  emailForm.subject = email.subject
  emailForm.content = email.content
  // Remove from history since we're editing it
  const index = emailHistory.value.findIndex((e: any) => e.id === email.id)
  if (index > -1) {
    emailHistory.value.splice(index, 1)
  }
}

const viewStats = (email: any) => {
  // TODO: Implement email statistics viewing
  console.log('Viewing stats for email:', email.id)
}

const getTemplateStatusBadge = (status: string) => {
  switch (status) {
    case 'active': return 'badge-success'
    case 'draft': return 'badge-warning'
    case 'archived': return 'badge-secondary'
    default: return 'badge-oil'
  }
}

const getEmailStatusBadge = (status: string) => {
  switch (status) {
    case 'sent': return 'badge-success'
    case 'draft': return 'badge-warning'
    case 'failed': return 'badge-danger'
    case 'sending': return 'badge-primary'
    default: return 'badge-oil'
  }
}

const formatDate = (date?: Date | string) => {
  if (!date) return 'Never'
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return 'Never'
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(parsed)
}

const formatTime = (date?: Date | string) => {
  if (!date) return '—'
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return '—'
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(parsed)
}

// Lifecycle
onMounted(async () => {
  // Initialize email management
  console.log('Email Management loaded')
  
  // Load actual data from email service
  await loadEmailStats()
  await loadEmailTemplates()
  // loadEmailHistory() // Keep empty for now as requested
})

// Data loading functions
const loadEmailStats = async () => {
  try {
    const stats = await emailService.getEmailStats()
    emailStats.value = stats
  } catch (error) {
    console.error('Failed to load email stats:', error)
  }
}

const loadEmailTemplates = async () => {
  try {
    const templates = await emailService.getTemplates()
    emailTemplates.value = Array.isArray(templates) ? templates : []
  } catch (error) {
    console.error('Failed to load email templates:', error)
  }
}
</script>

<style scoped>
/* Email specific styles */
.stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-emerald-500/30 transition-all duration-300;
}

.stat-icon {
  @apply w-12 h-12 rounded-xl flex items-center justify-center mb-4;
}

.stat-label {
  @apply text-sm font-medium text-slate-400 mb-2;
}

.stat-value {
  @apply text-3xl font-bold text-white mb-2;
}

.stat-change {
  @apply text-sm font-medium;
}

.form-group {
  @apply space-y-2;
}

.form-label {
  @apply block text-sm font-medium text-slate-300;
}

.form-input {
  @apply w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all duration-300;
}

.form-select {
  @apply w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all duration-300;
}

.form-select option {
  @apply bg-slate-700 text-white;
}

.btn-oilbuddy-primary {
  @apply bg-gradient-to-r from-emerald-400 to-cyan-400 hover:from-emerald-500 hover:to-cyan-500 text-slate-900 font-medium px-4 py-2 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl;
}

.btn-oilbuddy-primary:disabled {
  @apply opacity-50 cursor-not-allowed;
}
</style>
